import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config/env';
import Button from '../components/ui/Button';
import toast from 'react-hot-toast';

const stripePromise = loadStripe(config.stripe.publishableKey);

interface Listing {
  id: string;
  title: string;
  description: string;
  price: number;
  category: string;
  city: string;
  state: string;
  status: string;
  is_paid: boolean;
}

interface UserFreeUsage {
  freeListingsCount: number;
  canUseFree: boolean;
}

const Payment: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState<Listing | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [userFreeUsage, setUserFreeUsage] = useState<UserFreeUsage>({
    freeListingsCount: 0,
    canUseFree: true
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    fetchListing();
    fetchUserFreeUsage();
  }, [listingId, user, navigate]);

  const fetchListing = async () => {
    try {
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('id', listingId)
        .eq('user_id', user?.id)
        .single();

      if (error) {
        throw error;
      }

      setListing(data);
    } catch (error) {
      console.error('Error fetching listing:', error);
      toast.error('Error loading listing');
      navigate('/meus-anuncios');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserFreeUsage = async () => {
    try {
      const { data: freeListings, error } = await supabase
        .from('listings')
        .select('id')
        .eq('user_id', user?.id)
        .eq('is_paid', false)
        .eq('status', 'published');

      if (error) {
        console.error('Error fetching free usage:', error);
        return;
      }

      const freeListingsCount = freeListings?.length || 0;
      const canUseFree = freeListingsCount < 5;

      setUserFreeUsage({
        freeListingsCount,
        canUseFree
      });
    } catch (error) {
      console.error('Error fetching user free usage:', error);
    }
  };

  const handlePlanSelection = async (plan: 'free' | 'basic' | 'premium') => {
    if (!listing || !user) return;

    console.log('Starting plan selection:', plan);
    setIsProcessing(true);

    try {
      // Verificar se pode usar plano gratuito
      if (plan === 'free' && !userFreeUsage.canUseFree) {
        toast.error('You already used your 5 free days. Choose a paid plan.');
        return;
      }

      if (plan === 'free') {
        console.log('Activating free plan...');
        // Ativar plano gratuito
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 5); // 5 dias

        const { error } = await supabase
          .from('listings')
          .update({
            status: 'published',
            expires_at: expiresAt.toISOString(),
            is_paid: false,
            plan_type: 'free'
          })
          .eq('id', listing.id)
          .eq('user_id', user.id);

        if (error) {
          throw error;
        }

        toast.success('Listing published for free for 5 days!');
        navigate('/meus-anuncios');
        return;
      }

      console.log('Creating payment session for plan:', plan);
      // Para planos pagos, criar sessão do Stripe usando a API do Vercel
      const response = await fetch('/api/create-payment-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          listingId: listing.id,
          userId: user.id,
          plan: plan
        })
      });

      console.log('Resposta do servidor:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server error:', errorData);
        throw new Error(errorData.error || 'Error creating payment session');
      }

      const result = await response.json();
      console.log('Resultado do servidor:', result);

      if (result.url) {
        console.log('Redirecting to Stripe:', result.url);
        // Redirecionar para Stripe Checkout
        window.location.href = result.url;
      } else {
        throw new Error('Error processing payment');
      }

    } catch (error: any) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Error processing payment');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Listing not found</h2>
          <Button onClick={() => navigate('/meus-anuncios')}>
            Back to My Listings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Choose your Plan</h1>

          {/* Listing Summary */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Listing Summary</h2>
            
            <div className="space-y-3">
              <div>
                <span className="font-medium text-gray-700">Title:</span>
                <p className="text-gray-900">{listing.title}</p>
              </div>
              
              <div>
                <span className="font-medium text-gray-700">Price:</span>
                <p className="text-gray-900">R$ {listing.price.toLocaleString()}</p>
              </div>
              
              <div>
                <span className="font-medium text-gray-700">Category:</span>
                <p className="text-gray-900">{listing.category}</p>
              </div>
              
              <div>
                <span className="font-medium text-gray-700">Location:</span>
                <p className="text-gray-900">
                  {listing.city && listing.state ? `${listing.city}, ${listing.state}` : 'Not provided'}
                </p>
              </div>
            </div>
          </div>

          {/* Plans */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Plano Gratuito */}
            <div className={`border-2 rounded-lg p-6 ${userFreeUsage.canUseFree ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50 opacity-75'}`}>
              <div className="text-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Free</h3>
                <p className="text-3xl font-bold text-green-600">R$ 0</p>
                <p className="text-sm text-gray-500">5 days of exposure</p>
              </div>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-center text-sm text-gray-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Immediate publishing
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Basic visibility
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Email support
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  {userFreeUsage.freeListingsCount}/5 used
                </li>
              </ul>
              
              <Button
                onClick={() => handlePlanSelection('free')}
                disabled={!userFreeUsage.canUseFree || isProcessing}
                className={`w-full ${userFreeUsage.canUseFree ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'}`}
              >
                {isProcessing ? 'Processing...' : userFreeUsage.canUseFree ? 'Publish for Free' : 'Limit Reached'}
              </Button>
            </div>

            {/* Plano Básico */}
            <div className="border-2 border-blue-500 rounded-lg p-6 bg-blue-50">
              <div className="text-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Basic</h3>
                <p className="text-3xl font-bold text-blue-600">R$ 19,90</p>
                <p className="text-sm text-gray-500">30 days of exposure</p>
              </div>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-center text-sm text-gray-600">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Highlight in search
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Basic analytics
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Priority support
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Manual renewal
                </li>
              </ul>
              
              <Button
                onClick={() => handlePlanSelection('basic')}
                disabled={isProcessing}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {isProcessing ? 'Processing...' : 'Choose Basic'}
              </Button>
            </div>

            {/* Plano Premium */}
            <div className="border-2 border-purple-500 rounded-lg p-6 bg-purple-50">
              <div className="text-center mb-4">
                <div className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full mb-2 inline-block">
                  MOST POPULAR
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Premium</h3>
                <p className="text-3xl font-bold text-purple-600">R$ 49,90</p>
                <p className="text-sm text-gray-500">1 year of exposure</p>
              </div>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-center text-sm text-gray-600">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                  Maximum highlight
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                  Advanced analytics
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                  VIP support
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                  Automatic renewal
                </li>
              </ul>
              
              <Button
                onClick={() => handlePlanSelection('premium')}
                disabled={isProcessing}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {isProcessing ? 'Processing...' : 'Choose Premium'}
              </Button>
            </div>
          </div>

          {/* Additional Information */}
          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">💡 Important Information</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• You can use up to 5 free listings</li>
              <li>• Paid plans include highlights and analytics</li>
              <li>• Payments securely processed by Stripe</li>
              <li>• Automatic renewal available in the Premium plan</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment; 